import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './Header.css';

const NAV_ITEMS = [
  { label: 'Topics', dropdown: ['Green Card', 'Citizenship', 'Visas', 'Humanitarian'] },
  { label: 'Forms', dropdown: ['I-140G Gold Card', 'I-485', 'N-400', 'I-765'] },
  { label: 'Updates', dropdown: ['News', 'Policy Alerts', 'Processing Times'] },
  { label: 'Services', dropdown: ['Case Status', 'Change of Address', 'eNotification'] },
  { label: 'Programs', dropdown: ['Gold Card Program', 'EB-5', 'Military', 'Refugees'] },
  { label: 'Resources', dropdown: ['FAQ', 'Contact Us', 'Find an Office'] },
];

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setOpenDropdown(null);
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="site-header" ref={headerRef}>
      {/* Government Banner */}
      <div className="gov-banner">
        <div className="container">
          <div className="gov-banner-inner">
            {/* US Flag SVG */}
            <svg className="us-flag-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 190 100" aria-label="US Flag">
              <rect width="190" height="100" fill="#B22234" />
              <rect y="7.7" width="190" height="7.7" fill="white" />
              <rect y="23.1" width="190" height="7.7" fill="white" />
              <rect y="38.5" width="190" height="7.7" fill="white" />
              <rect y="53.8" width="190" height="7.7" fill="white" />
              <rect y="69.2" width="190" height="7.7" fill="white" />
              <rect y="84.6" width="190" height="7.7" fill="white" />
              <rect width="76" height="53.8" fill="#3C3B6E" />
              <g fill="white" fontSize="7">
                <text x="4" y="10">★</text><text x="12" y="10">★</text><text x="20" y="10">★</text><text x="28" y="10">★</text><text x="36" y="10">★</text><text x="44" y="10">★</text>
                <text x="8" y="18">★</text><text x="16" y="18">★</text><text x="24" y="18">★</text><text x="32" y="18">★</text><text x="40" y="18">★</text>
                <text x="4" y="26">★</text><text x="12" y="26">★</text><text x="20" y="26">★</text><text x="28" y="26">★</text><text x="36" y="26">★</text><text x="44" y="26">★</text>
                <text x="8" y="34">★</text><text x="16" y="34">★</text><text x="24" y="34">★</text><text x="32" y="34">★</text><text x="40" y="34">★</text>
                <text x="4" y="42">★</text><text x="12" y="42">★</text><text x="20" y="42">★</text><text x="28" y="42">★</text><text x="36" y="42">★</text><text x="44" y="42">★</text>
              </g>
            </svg>
            <span>An official website of the United States government</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="header-main">
        <div className="container header-main-inner">
          {/* Logo */}
          <Link to="/" className="header-logo">
            <img
              src="/images/logo.png"
              alt="U.S. Citizenship and Immigration Services"
              className="logo-img"
            />
            <div className="logo-text">
              <span className="logo-text-main">U.S. Citizenship</span>
              <span className="logo-text-sub">and Immigration Services</span>
            </div>
          </Link>

          {/* Search + Actions */}
          <div className="header-actions">
            <div className="search-bar">
              <input type="text" placeholder="Search our site" aria-label="Search" />
              <button aria-label="Submit search">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                </svg>
              </button>
            </div>
            <Link to="/" className="btn-home-link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              Home
            </Link>
            {user ? (
              <div className="user-menu-wrapper">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  {user.first_name} {user.last_name}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
                {userMenuOpen && (
                  <div className="user-dropdown">
                    <Link
                      to={user.role === 'admin' ? '/admin' : '/dashboard'}
                      onClick={() => setUserMenuOpen(false)}
                    >
                      {user.role === 'admin' ? 'Admin Panel' : 'My Dashboard'}
                    </Link>
                    <button onClick={handleLogout}>Sign Out</button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className="btn btn-secondary btn-sm">Sign In</Link>
                <Link to="/register" className="btn btn-primary btn-sm">Apply Now</Link>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="mobile-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <span className={`hamburger ${mobileOpen ? 'open' : ''}`} />
          </button>
        </div>
      </div>

      {/* Navigation — desktop only */}
      <nav className="site-nav desktop-nav" aria-label="Main navigation">
        <div className="container">
          <ul className="nav-list">
            {NAV_ITEMS.map((item) => (
              <li
                key={item.label}
                className="nav-item"
                onMouseEnter={() => setOpenDropdown(item.label)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button
                  className="nav-link"
                  onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                  aria-expanded={openDropdown === item.label}
                >
                  {item.label}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
                {openDropdown === item.label && (
                  <div className="dropdown">
                    <ul>
                      {item.dropdown.map((sub) => (
                        <li key={sub}>
                          <Link to="/" onClick={() => setOpenDropdown(null)}>{sub}</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Mobile menu — buttons only */}
      {mobileOpen && (
        <>
          <div className="mobile-overlay" onClick={() => setMobileOpen(false)} />
          <div className="mobile-menu">
            {user ? (
              <>
                <Link
                  to={user.role === 'admin' ? '/admin' : '/dashboard'}
                  className="mobile-menu-btn primary"
                  onClick={() => setMobileOpen(false)}
                >
                  {user.role === 'admin' ? 'Admin Panel' : 'My Dashboard'}
                </Link>
                <button className="mobile-menu-btn danger" onClick={() => { setMobileOpen(false); handleLogout(); }}>
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link to="/" className="mobile-menu-btn secondary" onClick={() => setMobileOpen(false)}>
                  Home
                </Link>
                <Link to="/login" className="mobile-menu-btn secondary" onClick={() => setMobileOpen(false)}>
                  Sign In
                </Link>
                <Link to="/register" className="mobile-menu-btn primary" onClick={() => setMobileOpen(false)}>
                  Apply Now
                </Link>
              </>
            )}
          </div>
        </>
      )}
    </header>
  );
}